/**
 * `createApiPipelineAlertsSource` — the `trigger-mgmt-api` `AlertsSource`.
 *
 * The shared `createApiAlertsSource` (`@authe/alerts`) is a pre-backend
 * placeholder: it PUT-upserts a camelCase `AlertRule` to `/alert-rules/{id}` and
 * reads a flat array. trigger-mgmt-api actually serves
 * `/v0/{triggers,destinations,alerts}` with **POST-create / PATCH-update**, the
 * snake_case `TriggerRuleRow` / `AlertDestination` shapes, and the standard
 * `{items, next_cursor}` page body. This dashboard-local source crosses that
 * camelCase ⇆ snake_case boundary (mirroring `pipelinesSource` / `tracersSource`)
 * so the rule + destination editors round-trip against the real API. Canonical
 * `/alert-*` paths are mapped onto those `/private/trigger-mgmt-api/v0/*` routes
 * by `RESOURCE_REWRITES` — the canonical names are deliberately unchanged, so
 * moving the surface off pipeline-mgmt-api was a rewrite-table edit.
 */
import { type ApiClient, unwrapData } from "@authe/fe-commons/api";

import { walkPages } from "./pagedList";
import { EventCriticality, criticalityFromName } from "@authe/fe-commons/events";
// `anyCondition` is a runtime value: import it from its real module. `@authe/alerts`
// only re-exports the trigger dictionary via `export * from "@authe/fe-commons/triggers"`,
// which esbuild can't surface as a static ESM named export (it works for the CJS
// build only), so a browser/ESM `import { anyCondition } from "@authe/alerts"` throws.
import { anyCondition } from "@authe/fe-commons/triggers";
import {
  type AlertChannel,
  type AlertDestination,
  type AlertDestinationConfig,
  type AlertEvent,
  type AlertHistoryPage,
  type AlertHistoryQuery,
  type AlertRule,
  type AlertsSource,
  type Condition,
} from "@authe/alerts";

const RULES_PATH = "/alert-rules";
const DESTINATIONS_PATH = "/alert-destinations";
const HISTORY_PATH = "/alert-history";

// -- severity ⇄ TriggerSeverity --------------------------------------------
// Backend `TriggerSeverity`: info | low | medium | high | critical. The FE
// `EventCriticality` adds a UI-only `success` band with no rule meaning, so it
// pins to the lowest backend severity on the way out.
const SEVERITY_TO_WIRE: Record<EventCriticality, string> = {
  [EventCriticality.CRITICAL]: "critical",
  [EventCriticality.HIGH]: "high",
  [EventCriticality.MEDIUM]: "medium",
  [EventCriticality.LOW]: "low",
  [EventCriticality.SUCCESS]: "info",
};

// -- rule wire ⇄ AlertRule -------------------------------------------------
interface RuleWire {
  id: string;
  name?: string;
  description?: string | null;
  enabled?: boolean;
  severity?: string;
  condition?: Condition;
  destination_ids?: string[];
  visible_to_team_ids?: string[];
}

function ruleFromWire(row: RuleWire): AlertRule {
  return {
    id: row.id,
    name: row.name ?? "",
    description: row.description ?? undefined,
    enabled: row.enabled ?? true,
    severity: criticalityFromName(row.severity ?? undefined, EventCriticality.MEDIUM),
    // An empty/absent condition is the backend "match any" node.
    condition: row.condition ?? anyCondition(),
    destinationIds: row.destination_ids ?? [],
    visibleToTeamIds: row.visible_to_team_ids ?? [],
  };
}

function ruleToWire(rule: AlertRule): Record<string, unknown> {
  return {
    name: rule.name,
    description: rule.description ?? null,
    enabled: rule.enabled,
    severity: SEVERITY_TO_WIRE[rule.severity] ?? "medium",
    condition: rule.condition ?? anyCondition(),
    destination_ids: rule.destinationIds,
    visible_to_team_ids: rule.visibleToTeamIds ?? [],
  };
}

// -- destination wire ⇄ AlertDestination -----------------------------------
// Backend `DestinationKind`: webhook | sns | email | slack | in_app | connector.
// The FE channel union covers webhook/email/slack/discord; the editors here only
// author those, so the remaining kinds map onto `webhook` for display.
interface DestinationWire {
  id: string;
  name?: string;
  enabled?: boolean;
  kind?: string;
  config?: Record<string, unknown>;
}

function channelFromKind(kind: string | undefined): AlertChannel {
  switch (kind) {
    case "email":
      return "email";
    case "slack":
      return "slack";
    case "discord":
      return "discord";
    default:
      return "webhook";
  }
}

function destinationConfigFromWire(
  kind: string | undefined,
  config: Record<string, unknown>,
): AlertDestinationConfig {
  switch (channelFromKind(kind)) {
    case "email":
      return {
        channel: "email",
        recipients: Array.isArray(config.recipients)
          ? (config.recipients as string[])
          : config.address
            ? [String(config.address)]
            : [],
      };
    case "slack":
      return {
        channel: "slack",
        webhookUrl: String(config.webhook_url ?? config.url ?? ""),
        channelName: config.channel ? String(config.channel) : undefined,
      };
    case "discord":
      return { channel: "discord", webhookUrl: String(config.webhook_url ?? config.url ?? "") };
    case "webhook":
    default:
      return { channel: "webhook", url: String(config.url ?? "") };
  }
}

function destinationFromWire(row: DestinationWire): AlertDestination {
  return {
    id: row.id,
    name: row.name ?? "",
    enabled: row.enabled ?? true,
    config: destinationConfigFromWire(row.kind, row.config ?? {}),
  };
}

function destinationToWire(dest: AlertDestination): Record<string, unknown> {
  const { config } = dest;
  // No initialisers: every arm below assigns both, `default` included, so a
  // seed value is only ever the shape a future arm forgets to overwrite.
  let kind: string;
  let wireConfig: Record<string, unknown>;
  switch (config.channel) {
    case "email":
      kind = "email";
      wireConfig = { recipients: config.recipients };
      break;
    case "slack":
      kind = "slack";
      wireConfig = { webhook_url: config.webhookUrl, channel: config.channelName };
      break;
    case "discord":
      kind = "discord";
      wireConfig = { webhook_url: config.webhookUrl };
      break;
    case "webhook":
    default:
      kind = "webhook";
      wireConfig = { url: config.url };
      break;
  }
  return { name: dest.name, enabled: dest.enabled, kind, config: wireConfig };
}

export function createApiPipelineAlertsSource(client: ApiClient): AlertsSource {
  // The editors author a client-side id before the backend mints one, so track
  // the ids the API already knows to pick POST (create) vs PATCH (update).
  const knownRuleIds = new Set<string>();
  const knownDestinationIds = new Set<string>();

  return {
    async listRules() {
      // Paged. `knownRuleIds` below is rebuilt from THIS list and decides
      // POST-vs-PATCH on save, so a rule the list never reached is a rule the
      // editor would re-create rather than update — and two enabled trigger
      // rules on one condition send the alert twice.
      const rows = await walkPages(client, RULES_PATH);
      knownRuleIds.clear();
      for (const row of rows) knownRuleIds.add(String(row.id));
      return rows.map((row) => ruleFromWire(row as unknown as RuleWire));
    },
    async saveRule(rule) {
      const body = ruleToWire(rule);
      const row = knownRuleIds.has(rule.id)
        ? unwrapData(await client.patch<RuleWire>(`${RULES_PATH}/${encodeURIComponent(rule.id)}`, body))
        : unwrapData(await client.post<RuleWire>(RULES_PATH, body));
      const saved = ruleFromWire(row);
      knownRuleIds.add(saved.id);
      // Drop the 30s GET cache so the caller's refetch reflects the write
      // (otherwise the new/updated rule won't appear until the TTL expires).
      client.invalidate(RULES_PATH);
      return saved;
    },
    async deleteRule(id) {
      await client.delete<void>(`${RULES_PATH}/${encodeURIComponent(id)}`);
      knownRuleIds.delete(id);
      client.invalidate(RULES_PATH);
    },

    async listDestinations() {
      // Paged, for the same reason as `listRules` one level down in
      // consequence: a destination past the first page saves as a duplicate,
      // and then every alert routed there is delivered twice.
      const rows = await walkPages(client, DESTINATIONS_PATH);
      knownDestinationIds.clear();
      for (const row of rows) knownDestinationIds.add(String(row.id));
      return rows.map((row) => destinationFromWire(row as unknown as DestinationWire));
    },
    async saveDestination(dest) {
      const body = destinationToWire(dest);
      const row = knownDestinationIds.has(dest.id)
        ? unwrapData(
            await client.patch<DestinationWire>(
              `${DESTINATIONS_PATH}/${encodeURIComponent(dest.id)}`,
              body,
            ),
          )
        : unwrapData(await client.post<DestinationWire>(DESTINATIONS_PATH, body));
      const saved = destinationFromWire(row);
      knownDestinationIds.add(saved.id);
      client.invalidate(DESTINATIONS_PATH);
      return saved;
    },
    async deleteDestination(id) {
      await client.delete<void>(`${DESTINATIONS_PATH}/${encodeURIComponent(id)}`);
      knownDestinationIds.delete(id);
      client.invalidate(DESTINATIONS_PATH);
    },

    async fetchHistory(query: AlertHistoryQuery): Promise<AlertHistoryPage> {
      const page = unwrapData(
        await client.get<{ items?: AlertEvent[]; next_cursor?: string | null }>(HISTORY_PATH, {
          // `rule_id` moves the read onto the by-rule index rather than the
          // tenant's history partition; omitted when absent so the unscoped
          // list keeps its existing cache key.
          query: { cursor: query.cursor, limit: query.limit, rule_id: query.ruleId },
        }),
      );
      return { items: page?.items ?? [], nextCursor: page?.next_cursor ?? null };
    },
  };
}
